import type { FitnessGroupType } from '../../../supabase/functions/_shared/fitnessWorkoutMetadata';
export interface WorkoutScheduleExercise {
  sets: readonly unknown[]; restSeconds?: number; groupId?: string; groupType?: FitnessGroupType;
}
export interface WorkoutSetPosition { exerciseIndex: number; setNumber: number }
export interface WorkoutSetTransition { next: WorkoutSetPosition | null; restSeconds: number; blockComplete: boolean }
const invalid = (): never => { throw new Error('invalid_fitness_workout_schedule'); };
const same = (a: WorkoutSetPosition, b: WorkoutSetPosition) => a.exerciseIndex === b.exerciseIndex && a.setNumber === b.setNumber;
/** Pure proposal only: building or reading a schedule never marks a set done. */
export function buildWorkoutSetSchedule(exercises: readonly WorkoutScheduleExercise[]): WorkoutSetPosition[] {
  if (!Array.isArray(exercises) || exercises.length > 32) return invalid();
  const result: WorkoutSetPosition[] = [], usedGroups = new Set<string>();
  for (let first = 0; first < exercises.length;) {
    const exercise = exercises[first];
    if (!exercise || !Array.isArray(exercise.sets) || exercise.sets.length < 1 || exercise.sets.length > 32) return invalid();
    if (exercise.groupType !== undefined && exercise.groupType !== 'superset' && exercise.groupType !== 'circuit') return invalid();
    if (!exercise.groupId) {
      if (exercise.groupType !== undefined || exercise.groupId !== undefined) return invalid();
      for (let setNumber = 1; setNumber <= exercise.sets.length; setNumber++) result.push({ exerciseIndex: first, setNumber });
      first++; continue;
    }
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(exercise.groupId) || usedGroups.has(exercise.groupId)) return invalid();
    usedGroups.add(exercise.groupId);
    let end = first + 1;
    while (end < exercises.length && exercises[end].groupId === exercise.groupId) end++;
    const members = exercises.slice(first, end), kind = exercise.groupType ?? 'superset';
    if (members.some(member => !Array.isArray(member.sets) || member.sets.length < 1 || member.sets.length > 32 || (member.groupType ?? 'superset') !== kind)) return invalid();
    const rounds = Math.max(...members.map(member => member.sets.length));
    for (let setNumber = 1; setNumber <= rounds; setNumber++) {
      for (let exerciseIndex = first; exerciseIndex < end; exerciseIndex++) {
        if (setNumber <= exercises[exerciseIndex].sets.length) result.push({ exerciseIndex, setNumber });
      }
    }
    first = end;
  }
  return result;
}
/** Group members run in rounds, with one shared rest after each round. */
export function getWorkoutSetTransition(exercises: readonly WorkoutScheduleExercise[], current: WorkoutSetPosition): WorkoutSetTransition {
  const schedule = buildWorkoutSetSchedule(exercises), index = schedule.findIndex(position => same(position, current));
  if (index < 0) return invalid();
  const next = schedule[index + 1] ?? null, exercise = exercises[current.exerciseIndex];
  const grouped = !!exercise.groupId, sameBlock = !!next && (grouped
    ? exercises[next.exerciseIndex].groupId === exercise.groupId
    : next.exerciseIndex === current.exerciseIndex);
  const blockComplete = !sameBlock;
  let restSeconds = 0;
  if (next) {
    if (!grouped) restSeconds = exercise.restSeconds ?? 60;
    else if (!sameBlock || next.setNumber !== current.setNumber) {
      restSeconds = Math.max(...exercises.filter(member => member.groupId === exercise.groupId).map(member => member.restSeconds ?? 60));
    }
  }
  if (!Number.isFinite(restSeconds) || restSeconds < 0 || restSeconds > 86400) return invalid();
  return { next, restSeconds, blockComplete };
}
